import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import Debug from 'debug';
import { asyncScheduler, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, throttleTime, withLatestFrom } from 'rxjs/operators';

import * as fromActions from '../actions/licence.actions';

import { Store } from '@ngrx/store';
import { selectCurrentOrganization } from '../reducers/organization.reducer';
import { LicenceService } from '../services/licence.service';

const debug = Debug('modeso:aumico-lib-users-fe:LicenceEffects');

@Injectable()
export class LicenceEffects {
    constructor(private readonly actions$: Actions, private readonly service: LicenceService, private store: Store) {}

    createLicencePacakge$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.licenceActions.createLicence.type),
            mergeMap(({ payload }) => {
                const { organizationId, dto } = payload;
                return this.service.createPackage(organizationId, dto).pipe(
                    map((data) => fromActions.licenceActions.createLicenceSuccess({ payload: data })),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.licenceActions.createLicenceFailure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateLicencePacakge$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.licenceActions.updateLicence.type),
            mergeMap(({ payload }) => {
                return this.service.updatePackage$(payload).pipe(
                    map((data) => fromActions.licenceActions.updateLicenceSuccess({ payload: data })),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.licenceActions.updateLicenceFailure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    requestLicenceUpgrade$ = createEffect(() =>
        this.actions$.pipe(
            // 1) Listen for the action creator itself
            ofType(fromActions.licenceActions.requestUpdateLicence),

            // 2) Grab the currentOrganization from state
            withLatestFrom(this.store.select(selectCurrentOrganization)),

            // 3) Only proceed if we have an org object
            filter(([_, org]) => !!org),

            // 4) Extract just the org ID
            map(([_, org]) => org.id),

            // 6) Never more often than once per 500ms
            throttleTime(500, asyncScheduler, { leading: false, trailing: true }),

            // 7) Finally switch into your HTTP call
            switchMap((orgId) =>
                this.service.requestLicenceUpgrade$(orgId).pipe(
                    map((status) => fromActions.licenceActions.requestLicenceSuccess()),
                    catchError((error: HttpErrorResponse) =>
                        of(
                            fromActions.licenceActions.requestLicenceFailure({
                                payload: error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    loadLicenceStatus$ = createEffect(() =>
        this.actions$.pipe(
            // 1) Listen for the action creator itself
            ofType(fromActions.licenceActions.getLicenceStatus),

            // 2) Grab the currentOrganization from state
            withLatestFrom(this.store.select(selectCurrentOrganization)),

            // 3) Only proceed if we have an org object
            filter(([_, org]) => !!org),

            // 4) Extract just the org ID
            map(([_, org]) => org.id),

            // 6) Never more often than once per 500ms
            throttleTime(500, asyncScheduler, { leading: false, trailing: true }),

            // 7) Finally switch into your HTTP call
            switchMap((orgId) =>
                this.service.getLicenceStatus$(orgId).pipe(
                    map((status) => fromActions.licenceActions.getLicenceStatusSuccess({ payload: status })),
                    catchError((error: HttpErrorResponse) =>
                        of(
                            fromActions.licenceActions.getLicenceStatusFailure({
                                payload: error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    deleteLicencePacakge$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.licenceActions.deleteLicence.type),
            mergeMap(({ packageId, organizationId }) => {
                return this.service.deletePackage$(packageId, organizationId).pipe(
                    map(() => fromActions.licenceActions.deleteLicenceSuccess({ packageId, organizationId })),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.licenceActions.deleteLicenceFailure({ payload: error }));
                    }),
                );
            }),
        ),
    );
}
